import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Layout, Menu, Avatar, Typography, Button, Popconfirm } from 'antd';
import { useAuth } from '../contexts/AuthContext';
import { useCanAccessInstrumentStock } from '../hooks/useCanAccessInstrumentStock';
import type { MenuProps } from 'antd';
import { LimsNavIcon } from './LimsNavIcon';
import { resolveUserAvatarSrc } from '../utils/resolveUploadAssetUrl';
import './Sidebar.css';

const { Sider } = Layout;
const { Text } = Typography;

interface MenuItem {
  key: string;
  icon: React.ReactNode;
  label: string;
  path?: string;
  children?: MenuProps['items'];
}

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

const subMenuDotIcon = (
  <span className="submenu-dot" aria-hidden>
    <span className="submenu-dot-inner" />
  </span>
);

const Sidebar: React.FC<SidebarProps> = ({ isCollapsed, onToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, username, role, realName, avatar, teamId } = useAuth();
  const { canAccessStock: canAccessInstrumentStock } = useCanAccessInstrumentStock();
  const avatarSrc = resolveUserAvatarSrc(avatar);
  const [isMobile, setIsMobile] = useState(false);
  const [openKeys, setOpenKeys] = useState<string[]>(() => {
    const keys: string[] = [];
    const p = location.pathname;
    if (p.startsWith('/literature')) {
      keys.push('literature');
    }
    if (p.startsWith('/feedback') || p.startsWith('/admin/feedback')) keys.push('feedback');
    if (p.startsWith('/instruments') || p.startsWith('/workbench/work-hours')) keys.push('instruments');
    if (
      p.startsWith('/workbench/application-submission') ||
      p.startsWith('/workbench/approval-management') ||
      p.startsWith('/workbench/approval-approver-settings') ||
      p.startsWith('/workbench/team-billing')
    ) {
      keys.push('approval-center');
    }
    if (p.startsWith('/lab-activities')) keys.push('lab-activities');
    return keys;
  });

  const hasLiteraturePermission = () => true;

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 480);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    setOpenKeys((prev) => {
      const next = new Set(prev);
      const p = location.pathname;
      if (p.startsWith('/literature')) {
        next.add('literature');
      }
      if (p.startsWith('/workbench/experiment-records')) {
        next.add('experiment-records-menu');
      }
      if (p.startsWith('/feedback') || p.startsWith('/admin/feedback')) next.add('feedback');
      if (p.startsWith('/instruments') || p.startsWith('/workbench/work-hours')) next.add('instruments');
      if (
        p.startsWith('/workbench/application-submission') ||
        p.startsWith('/workbench/approval-management') ||
        p.startsWith('/workbench/approval-approver-settings') ||
        p.startsWith('/workbench/team-billing')
      ) {
        next.add('approval-center');
      }
      if (p.startsWith('/lab-activities')) next.add('lab-activities');
      return Array.from(next);
    });
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getLiteratureSubMenu = (): MenuProps['items'] => [
    {
      key: '/literature',
      icon: subMenuDotIcon,
      label: '文献库'
    },
    {
      key: '/literature/shared',
      icon: subMenuDotIcon,
      label: '共享文献'
    },
    {
      key: '/literature/notebook',
      icon: subMenuDotIcon,
      label: '笔记本'
    }
  ];

  const menuItems: MenuItem[] = [
    {
      key: '/dashboard',
      icon: <LimsNavIcon name="dashboard" />,
      label: '仪表盘',
      path: '/dashboard'
    },
    {
      key: '/members',
      icon: <LimsNavIcon name="group" />,
      label: '成员管理',
      path: '/members'
    },
    {
      key: 'experiment-records-menu',
      icon: <LimsNavIcon name="note_stack" />,
      label: '实验记录本'
    },
    {
      key: '/consumables',
      icon: <LimsNavIcon name="inventory_2" />,
      label: '耗材管理',
      path: '/consumables'
    },
    {
      key: '/reagents',
      icon: <LimsNavIcon name="science" />,
      label: '试剂管理',
      path: '/reagents'
    },
    {
      key: 'instruments',
      icon: <LimsNavIcon name="biotech" />,
      label: '仪器管理'
    },
    {
      key: 'lab-activities',
      icon: <LimsNavIcon name="event" />,
      label: '实验室活动'
    },
    {
      key: 'approval-center',
      icon: <LimsNavIcon name="verified_user" />,
      label: '审批中心'
    },
    {
      key: 'feedback',
      icon: <LimsNavIcon name="forum" />,
      label: '畅所欲言'
    },
    {
      key: 'literature',
      icon: <LimsNavIcon name="menu_book" />,
      label: '文献管理'
    },
    {
      key: '/knowledge-base',
      icon: <LimsNavIcon name="library_books" />,
      label: '知识库',
      path: '/knowledge-base'
    },
    {
      key: '/profile',
      icon: <LimsNavIcon name="account_circle" />,
      label: '个人中心',
      path: '/profile'
    }
  ];

  const adminMenuItems: MenuItem[] = [];

  const getMenuItems = (): MenuItem[] => {
    const baseItems = menuItems.filter(
      (item) =>
        ![
          'literature',
          'feedback',
          'instruments',
          'approval-center',
          'lab-activities',
          'experiment-records-menu'
        ].includes(item.key)
    );
    const items: MenuItem[] = [...baseItems];

    const experimentMenuBase = menuItems.find((item) => item.key === 'experiment-records-menu');
    if (experimentMenuBase) {
      const experimentItem: MenuItem = {
        ...experimentMenuBase,
        children: [
          { key: '/workbench/experiment-records', icon: subMenuDotIcon, label: '全部记录' },
          { key: '/workbench/experiment-records/shared', icon: subMenuDotIcon, label: '共享记录本' }
        ]
      };
      const membersIndex = items.findIndex((item) => item.key === '/members');
      if (membersIndex >= 0) {
        items.splice(membersIndex + 1, 0, experimentItem);
      } else {
        items.unshift(experimentItem);
      }
    }

    const instrumentsMenu = menuItems.find((item) => item.key === 'instruments');
    if (instrumentsMenu) {
      const instrumentsItem: MenuItem = {
        ...instrumentsMenu,
        children: [
          { key: '/instruments', icon: subMenuDotIcon, label: '仪器展示' },
          ...(canAccessInstrumentStock
            ? [{ key: '/instruments/stock', icon: subMenuDotIcon, label: '库存管理' }]
            : []),
          { key: '/instruments/work-hours', icon: subMenuDotIcon, label: '工作时长' },
          { key: '/instruments/shared-platform', icon: subMenuDotIcon, label: '共享平台' }
        ]
      };
      const reagentsIndex = items.findIndex((item) => item.key === '/reagents');
      if (reagentsIndex >= 0) {
        items.splice(reagentsIndex + 1, 0, instrumentsItem);
      } else {
        items.push(instrumentsItem);
      }
    }

    const approvalMenu = menuItems.find((item) => item.key === 'approval-center');
    if (approvalMenu) {
      const approvalItem: MenuItem = {
        ...approvalMenu,
        children: [
          {
            key: '/workbench/application-submission',
            icon: subMenuDotIcon,
            label: '发起申请'
          },
          {
            key: '/workbench/approval-management',
            icon: subMenuDotIcon,
            label: '审批管理'
          },
          ...(role === '管理员'
            ? [
                {
                  key: '/workbench/approval-approver-settings',
                  icon: subMenuDotIcon,
                  label: '审批人设置'
                },
                {
                  key: '/workbench/team-billing',
                  icon: subMenuDotIcon,
                  label: '团队账单'
                }
              ]
            : [])
        ]
      };
      const instrumentsIndex = items.findIndex((item) => item.key === 'instruments');
      if (instrumentsIndex >= 0) {
        items.splice(instrumentsIndex + 1, 0, approvalItem);
      } else {
        const reagentsIndex = items.findIndex((item) => item.key === '/reagents');
        if (reagentsIndex >= 0) {
          items.splice(reagentsIndex + 1, 0, approvalItem);
        } else {
          items.push(approvalItem);
        }
      }
    }

    const labActivitiesMenu = menuItems.find((item) => item.key === 'lab-activities');
    if (labActivitiesMenu) {
      const labActivitiesItem: MenuItem = {
        ...labActivitiesMenu,
        children: [
          {
            key: '/lab-activities/create-activity',
            icon: subMenuDotIcon,
            label: '发起活动'
          },
          {
            key: '/lab-activities/duty-schedule',
            icon: subMenuDotIcon,
            label: '值日安排'
          }
        ]
      };
      const approvalAnchorIndex = items.findIndex((item) => item.key === 'approval-center');
      if (approvalAnchorIndex >= 0) {
        items.splice(approvalAnchorIndex + 1, 0, labActivitiesItem);
      } else {
        const instrumentsIndex = items.findIndex((item) => item.key === 'instruments');
        if (instrumentsIndex >= 0) {
          items.splice(instrumentsIndex + 1, 0, labActivitiesItem);
        } else {
          items.push(labActivitiesItem);
        }
      }
    }

    if (hasLiteraturePermission()) {
      const literatureItem: MenuItem = {
        ...menuItems.find((item) => item.key === 'literature')!,
        children: getLiteratureSubMenu()
      };

      const approvalAnchorIndex = items.findIndex((item) => item.key === 'approval-center');
      if (approvalAnchorIndex >= 0) {
        items.splice(approvalAnchorIndex + 1, 0, literatureItem);
      } else {
        items.push(literatureItem);
      }
    }

    const feedbackBase = menuItems.find((item) => item.key === 'feedback');
    if (feedbackBase) {
      const feedbackChildren: MenuProps['items'] = [
        {
          key: '/feedback/square',
          icon: subMenuDotIcon,
          label: '匿名广场'
        },
        {
          key: '/feedback/my',
          icon: subMenuDotIcon,
          label: '我的匿名记录'
        }
      ];

      const showMainAppFeedbackAdmin =
        role === '管理员' || (role === '超级管理员' && !!teamId);
      if (showMainAppFeedbackAdmin) {
        feedbackChildren.push(
          {
            key: '/admin/feedback/review',
            icon: subMenuDotIcon,
            label: '内容审核队列'
          },
          {
            key: '/admin/feedback/analytics',
            icon: subMenuDotIcon,
            label: '数据看板'
          }
        );
        if (role === '超级管理员') {
          feedbackChildren.push({
            key: '/admin/feedback/settings',
            icon: subMenuDotIcon,
            label: '敏感词配置'
          });
        }
      }

      const feedbackItem: MenuItem = {
        ...feedbackBase,
        children: feedbackChildren
      };

      const approvalAnchorIndex = items.findIndex((item) => item.key === 'approval-center');
      if (approvalAnchorIndex >= 0) {
        items.splice(approvalAnchorIndex + 1, 0, feedbackItem);
      } else {
        items.push(feedbackItem);
      }
    }

    if (role === '管理员') {
      return [...items, ...adminMenuItems];
    }
    return items;
  };

  const getSelectedKey = () => {
    const currentPath = location.pathname;
    if (currentPath.startsWith('/literature')) {
      if (currentPath.startsWith('/literature/shared')) return ['/literature/shared'];
      if (currentPath.startsWith('/literature/notebook')) return ['/literature/notebook'];
      return ['/literature'];
    }
    if (currentPath.startsWith('/knowledge-base')) {
      return ['/knowledge-base'];
    }
    if (currentPath.startsWith('/feedback') || currentPath.startsWith('/admin/feedback')) {
      return [currentPath];
    }
    if (currentPath.startsWith('/instruments/stock')) {
      return ['/instruments/stock'];
    }
    if (currentPath.startsWith('/instruments/shared-platform')) {
      return ['/instruments/shared-platform'];
    }
    if (currentPath.startsWith('/instruments/work-hours')) {
      return ['/instruments/work-hours'];
    }
    if (currentPath.startsWith('/workbench/work-hours')) {
      return ['/instruments/work-hours'];
    }
    if (currentPath.startsWith('/instruments')) {
      return ['/instruments'];
    }
    if (currentPath.startsWith('/workbench/application-submission')) {
      return ['/workbench/application-submission'];
    }
    if (currentPath.startsWith('/workbench/approval-management')) {
      return ['/workbench/approval-management'];
    }
    if (currentPath.startsWith('/workbench/approval-approver-settings')) {
      return ['/workbench/approval-approver-settings'];
    }
    if (currentPath.startsWith('/workbench/team-billing')) {
      return ['/workbench/team-billing'];
    }
    if (currentPath.startsWith('/workbench/experiment-records/shared')) {
      return ['/workbench/experiment-records/shared'];
    }
    if (currentPath.startsWith('/workbench/experiment-records')) {
      return ['/workbench/experiment-records'];
    }
    if (currentPath.startsWith('/lab-activities')) {
      return [currentPath];
    }
    const allMenuItems = getMenuItems();
    const matchedItem = allMenuItems.find(
      (item: MenuItem) =>
        item.path === currentPath ||
        (item.path !== '/dashboard' && currentPath.startsWith(item.path || ''))
    );
    return matchedItem ? [matchedItem.key] : ['/dashboard'];
  };

  const handleMenuClick = ({ key }: { key: string }) => {
    if (key.startsWith('/literature')) {
      navigate(key);
      if (isMobile && !isCollapsed) onToggle();
      return;
    }
    if (key.startsWith('/feedback') || key.startsWith('/admin/feedback')) {
      navigate(key);
      if (isMobile && !isCollapsed) onToggle();
      return;
    }
    if (key.startsWith('/instruments')) {
      navigate(key);
      if (isMobile && !isCollapsed) onToggle();
      return;
    }
    if (
      key.startsWith('/workbench/application-submission') ||
      key.startsWith('/workbench/approval-management') ||
      key.startsWith('/workbench/approval-approver-settings') ||
      key.startsWith('/workbench/team-billing') ||
      key.startsWith('/workbench/experiment-records')
    ) {
      navigate(key);
      if (isMobile && !isCollapsed) onToggle();
      return;
    }
    if (key.startsWith('/lab-activities')) {
      navigate(key);
      if (isMobile && !isCollapsed) onToggle();
      return;
    }
    const allMenuItems = getMenuItems();
    const item = allMenuItems.find((m: MenuItem) => m.key === key);
    if (item && item.path) {
      navigate(item.path);
      if (isMobile && !isCollapsed) onToggle();
    }
  };

  const handleOpenChange = (keys: string[]) => {
    setOpenKeys(keys);
  };

  return (
    <Sider
      trigger={null}
      collapsible
      collapsed={isCollapsed}
      className="antd-sidebar lims-sidebar"
      width={256}
      collapsedWidth={64}
    >
      {!isCollapsed && (
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <div className="lims-logo-mark lims-logo-mark--img" aria-hidden>
              <img
                src={`${process.env.PUBLIC_URL || ''}/brand/labmind-logo.png`}
                alt=""
                className="lims-sidebar-logo-img"
              />
            </div>
            <div className="sidebar-brand-titles">
              <Text className="logo-text" strong>
                LabMind
              </Text>
              <Text className="logo-tagline">Precision Lab</Text>
            </div>
          </div>
          <Button
            type="text"
            icon={<LimsNavIcon name="left_panel_close" sizePx={20} />}
            onClick={onToggle}
            className="sidebar-toggle"
          />
        </div>
      )}

      {isCollapsed && (
        <div className="collapsed-expand-button">
          <Button
            type="text"
            icon={<LimsNavIcon name="left_panel_open" sizePx={20} />}
            onClick={onToggle}
            className="expand-toggle"
            size="large"
          />
        </div>
      )}

      <Menu
        theme="light"
        mode="inline"
        inlineCollapsed={isCollapsed}
        selectedKeys={getSelectedKey()}
        openKeys={isCollapsed ? [] : openKeys}
        onOpenChange={handleOpenChange}
        onClick={handleMenuClick}
        className="sidebar-menu"
        items={getMenuItems().map((item: MenuItem) => {
          if (item.children) {
            return {
              key: item.key,
              icon: item.icon,
              label: <span className="menu-link">{item.label}</span>,
              children: item.children
            };
          }
          return {
            key: item.key,
            icon: item.icon,
            label: (
              <Link
                to={item.path || '#'}
                className="menu-link"
                onClick={() => {
                  if (isMobile && !isCollapsed) onToggle();
                }}
              >
                {item.label}
              </Link>
            )
          };
        })}
      />

      {!isCollapsed && (
        <div className="sidebar-footer">
          <div className="user-info">
            <Avatar
              size={40}
              src={avatarSrc}
              icon={<LimsNavIcon name="account_circle" sizePx={24} />}
              style={{
                backgroundColor: avatar ? 'transparent' : 'rgba(0, 53, 95, 0.12)',
                color: avatar ? undefined : 'var(--lims-primary)'
              }}
            />
            <div className="user-details">
              <Text className="user-name" strong>
                {realName || username || '管理员'}
              </Text>
              <Text className="user-role" type="secondary">
                {role || '超级管理员'}
              </Text>
            </div>
            <Popconfirm
              title="确定要退出登录吗？"
              description="退出后将返回首页"
              onConfirm={handleLogout}
              okText="确定"
              cancelText="取消"
              placement="topRight"
            >
              <Button
                type="text"
                icon={<LimsNavIcon name="logout" sizePx={18} />}
                className="logout-button"
                size="small"
              />
            </Popconfirm>
          </div>
        </div>
      )}
    </Sider>
  );
};

export default Sidebar;
